import type { OrderedListStyle } from "../schema/index.js";

export interface AlphaListMarker {
  style: OrderedListStyle;
  order: number;
}

export interface Preprocessed {
  source: string;
  /** Source line index → the alphabetic marker that line originally carried. */
  alphaMarkers: Map<number, AlphaListMarker>;
}

const FENCE = /^(\s{0,3})(`{3,}|~{3,})/;
const ALPHA_ITEM = /^(\s{0,7})([a-zA-Z])([.)])(\s)/;
const ANY_ITEM = /^(\s*)([-*+]|\d+[.)]|[a-zA-Z][.)])\s/;
const ANY_ALPHA_ITEM = /^[ \t]{0,7}[a-zA-Z][.)]\s/m;

function letterOrder(letter: string): number {
  const lower = letter.toLowerCase();
  return lower.charCodeAt(0) - 96;
}

/**
 * markdown-it only accepts digits as ordered-list markers, so alphabetic
 * markers are rewritten to their numeric position before parsing and the
 * original style is remembered per line. A letter only counts as a marker when
 * the previous line is blank, another list line, or itself a rewritten marker —
 * otherwise prose such as "a. maybe" would start a list mid-paragraph.
 */
export function preprocessAlphaLists(text: string): Preprocessed {
  // Most documents have no letter markers at all; one scan avoids splitting
  // and re-joining the whole source for them.
  if (!ANY_ALPHA_ITEM.test(text)) return { source: text, alphaMarkers: new Map() };
  const lines = text.split("\n");
  const alphaMarkers = new Map<number, AlphaListMarker>();
  let fence: string | null = null;
  let previousWasMarker = false;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const fenceMatch = FENCE.exec(line);
    if (fence !== null) {
      if (fenceMatch && line.trim().startsWith(fence)) fence = null;
      previousWasMarker = false;
      continue;
    }
    if (fenceMatch?.[2]) {
      fence = fenceMatch[2].slice(0, 3);
      previousWasMarker = false;
      continue;
    }

    const match = ALPHA_ITEM.exec(line);
    const previous = i === 0 ? "" : (lines[i - 1] ?? "");
    const previousQualifies =
      i === 0 || previous.trim() === "" || previousWasMarker || ANY_ITEM.test(previous);
    if (match && previousQualifies) {
      const indent = match[1] ?? "";
      const letter = match[2] ?? "a";
      const delimiter = match[3] ?? ".";
      const space = match[4] ?? " ";
      const order = letterOrder(letter);
      alphaMarkers.set(i, {
        style: letter === letter.toLowerCase() ? "lower-alpha" : "upper-alpha",
        order,
      });
      lines[i] = `${indent}${String(order)}${delimiter}${space}${line.slice(match[0].length)}`;
      previousWasMarker = true;
      continue;
    }
    previousWasMarker = false;
  }

  return { source: lines.join("\n"), alphaMarkers };
}

const BARE_CHECKBOX = /^( ?)(\[[ xX_-]\] )/;

/**
 * Rewrites applied to plain text pasted into the editor before it is parsed as
 * markdown. Deliberately NOT part of `parseMarkdown`: markdown arriving through
 * the API is parsed verbatim.
 */
export function normalizePastedMarkdown(text: string): string {
  const lines = text
    .split("\n")
    .map((line) => (BARE_CHECKBOX.test(line) ? line.replace(BARE_CHECKBOX, "- $2") : line));
  return lines.join("\n").replace(/\n{3,}/g, "\n\n\\\n");
}
